import React, { useState, useEffect } from "react";
import { proxyAvatar } from "../../helper/ImageProxy";
import { Button, Text, TextInput, ScrollArea, Flex, Pagination } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { Editor } from "@tiptap/react";

export interface TagUser {
  id: number;
  userName: string;
  name: string;
  avatar?: string;
  email?: string;
  address?: string;
  phone?: string;
  isAll?: boolean;
}

interface TagUsersSectionProps {
  tagUsers: TagUser[];
  editor: Editor | null;
  USERS_PER_PAGE: number;
}

export const TagUsersSection = ({
  tagUsers,
  editor,
  USERS_PER_PAGE,
}: TagUsersSectionProps) => {
  const [tagUserSearch, setTagUserSearch] = useState<string>("");
  const [debouncedTagUserSearch] = useDebouncedValue(tagUserSearch, 500);
  const [tagUserPage, setTagUserPage] = useState<number>(1);

  useEffect(() => {
    setTagUserPage(1);
  }, [debouncedTagUserSearch]);

  const filteredUsers = tagUsers.filter(
    (user) =>
      user.name?.toLowerCase().includes(debouncedTagUserSearch.toLowerCase()) ||
      user.userName?.toLowerCase().includes(debouncedTagUserSearch.toLowerCase())
  );
  const totalPagesCalc = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsersCalc = filteredUsers.slice(
    (tagUserPage - 1) * USERS_PER_PAGE,
    tagUserPage * USERS_PER_PAGE
  );

  return (
    <div
      style={{
        marginTop: "16px",
        padding: "16px",
        backgroundColor: "#f8f9fa",
        borderRadius: "12px",
        border: "1px solid #e9ecef",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <Text size="sm" fw={600} c="dark.7">
          🏷️ Gắn thẻ người dùng:
        </Text>
        <Button
          variant="subtle"
          color="red"
          size="xs"
          onClick={() => {
            const content = editor?.getHTML() || "";
            const cleanContent = content.replace(/@\([^)]+\)\s*/g, "");
            editor?.commands.setContent(cleanContent);
          }}
        >
          Xóa tất cả tag
        </Button>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <Button
          variant="gradient"
          gradient={{ from: "blue", to: "cyan" }}
          size="sm"
          leftSection={
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                backgroundColor: "#228be6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                color: "white",
              }}
            >
              ALL
            </div>
          }
          onClick={() => {
            editor?.commands.insertContent("@(Báo cho cả nhóm) ");
          }}
          style={{ marginRight: "12px", marginBottom: "8px" }}
        >
          Báo cho cả nhóm
        </Button>
      </div>

      <TextInput
        placeholder="Tìm kiếm theo tên hoặc username..."
        value={tagUserSearch}
        onChange={(e) => setTagUserSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        mb="md"
        styles={{ input: { borderRadius: "8px" } }}
      />

      <ScrollArea.Autosize mah={350} scrollbars="y">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "12px",
          }}
        >
          {paginatedUsersCalc.map((user) => {
            const currentContent = editor?.getHTML() || "";
            const isTagged = currentContent.includes(`@(${user.name})`);

            return (
              <div
                key={user.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: user.avatar ? "transparent" : "#e9ecef",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#495057",
                    marginRight: "12px",
                    flexShrink: 0,
                    overflow: "hidden",
                    border: "2px solid #f8f9fa",
                  }}
                >
                  {user.avatar ? (
                    <img
                      src={proxyAvatar(user.avatar, 40)}
                      alt={user.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement!.style.backgroundColor = "#e9ecef";
                        e.currentTarget.parentElement!.innerHTML = user.name?.charAt(0)?.toUpperCase() || "U";
                      }}
                    />
                  ) : (
                    user.name?.charAt(0)?.toUpperCase() || "U"
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} style={{ color: "#212529", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user.name}
                  </Text>
                  <Text size="xs" c="dimmed" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    @{user.userName}
                  </Text>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  {!isTagged ? (
                    <Button size="xs" variant="light" color="blue" onClick={() => editor?.commands.insertContent(`@(${user.name}) `)} style={{ fontSize: "10px", padding: "4px 8px", height: "auto" }}>
                      Tag
                    </Button>
                  ) : (
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => {
                        const content = editor?.getHTML() || "";
                        const regex = new RegExp(`@\\(${user.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\)\\s*`, "g");
                        editor?.commands.setContent(content.replace(regex, ""));
                      }}
                      style={{ fontSize: "10px", padding: "4px 8px", height: "auto" }}
                    >
                      Xóa tag
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea.Autosize>

      {filteredUsers.length > USERS_PER_PAGE ? (
        <Flex justify="space-between" align="center" mt="md">
          <Text size="sm" c="dimmed">
            Hiển thị {(tagUserPage - 1) * USERS_PER_PAGE + 1} - {Math.min(tagUserPage * USERS_PER_PAGE, filteredUsers.length)} / {filteredUsers.length} người dùng
          </Text>
          <Pagination value={tagUserPage} onChange={setTagUserPage} total={totalPagesCalc} size="sm" radius="md" />
        </Flex>
      ) : (
        <Text size="sm" c="dimmed" mt="md">Tổng: {filteredUsers.length} người dùng</Text>
      )}

      {tagUsers.length === 0 && <Text size="sm" c="dimmed" ta="center" py="xl">Không có người dùng nào để gắn thẻ</Text>}
      {tagUsers.length > 0 && filteredUsers.length === 0 && <Text size="sm" c="dimmed" ta="center" py="xl">Không tìm thấy người dùng phù hợp</Text>}
    </div>
  );
};
